import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, alpha } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

const StatsCard = ({
  title,
  value,
  icon,
  color = 'primary',
  trend,
  trendValue,
  subtitle,
  loading = false,
  onClick,
}) => {
  const colorMap = {
    primary: { main: '#3b82f6', light: '#dbeafe', dark: '#1d4ed8' },
    secondary: { main: '#8b5cf6', light: '#ede9fe', dark: '#6d28d9' },
    success: { main: '#10b981', light: '#d1fae5', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fef3c7', dark: '#d97706' },
    error: { main: '#ef4444', light: '#fee2e2', dark: '#dc2626' },
    info: { main: '#06b6d4', light: '#cffafe', dark: '#0891b2' },
  };

  const selectedColor = colorMap[color] || colorMap.primary;

  if (loading) {
    return (
      <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={120} height={48} sx={{ mt: 1 }} />
              <Skeleton variant="text" width={100} height={16} sx={{ mt: 1 }} />
            </Box>
            <Skeleton variant="circular" width={56} height={56} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease-in-out',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: (theme) => theme.shadows[4],
        } : {},
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${selectedColor.main} 0%, ${selectedColor.light} 100%)`,
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                mt: 1,
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {(trend || subtitle) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                {trend && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: trend === 'up' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                    }}
                  >
                    {trend === 'up' ? (
                      <TrendingUp sx={{ fontSize: 16, color: '#10b981' }} />
                    ) : (
                      <TrendingDown sx={{ fontSize: 16, color: '#ef4444' }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: trend === 'up' ? '#059669' : '#dc2626',
                      }}
                    >
                      {trendValue}
                    </Typography>
                  </Box>
                )}
                {subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${selectedColor.light} 0%, ${alpha(selectedColor.main, 0.2)} 100%)`,
              color: selectedColor.dark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 32 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatsCard;
